import { useEffect } from "react";
import { Conversation } from "@/types";
import { ChatNavState } from "./useProjectBoundaryReset";

interface UseLoadConversationHistoryOptions {
  projectId: string | undefined;
  navStateRef: React.MutableRefObject<ChatNavState>;
  getConversationsByProjectId: (projectId: string) => Conversation[];
  switchConversation: (conversationId: string) => void;
  loadConversationHistory: (projectId: string) => Promise<string | null>;
}

/**
 * For existing projects, fetches past conversation messages from the API.
 * Skips for new projects (no history exists yet).
 *
 * Deduplication is handled by two mechanisms:
 *   1. `getConversationsByProjectId` checks if data is already in state.
 *   2. The `cancelled` flag discards stale responses from unmounted effect
 *      instances (e.g., during React StrictMode remounts in development).
 */
export function useLoadConversationHistory({
  projectId,
  navStateRef,
  getConversationsByProjectId,
  switchConversation,
  loadConversationHistory,
}: UseLoadConversationHistoryOptions): void {
  useEffect(() => {
    if (!projectId || navStateRef.current?.isNewProject) return;
    let cancelled = false;

    // Check if we already have conversations loaded for this project
    const projectConversations = getConversationsByProjectId(projectId);
    if (projectConversations.length > 0) {
      // Switch to the most recent conversation for this project
      switchConversation(projectConversations[0].id);
      return;
    }

    // Load conversation history from the API
    // loadConversationHistory returns the conversation ID directly,
    // avoiding stale closure issues with getConversationsByProjectId
    loadConversationHistory(projectId)
      .then((conversationId) => {
        if (!cancelled && conversationId) {
          switchConversation(conversationId);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load conversation history:", error);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    getConversationsByProjectId,
    switchConversation,
    loadConversationHistory,
  ]);
}
